// Role walkthroughs and tooltips. The copy explains how Pass works for each role; it states no lending
// policy, underwriting rule or price (AGENTS.md). Progress is stored per user and role on the server.
import type { Role } from "./types";

export interface WalkthroughStep {
  title: string;
  body: string;
  /** The `data-tour` id of the control this step is about, when it is on the role home. */
  target?: TourTarget;
}

export type TourTarget = "role-switcher" | "stop-acting" | "prepared-queue" | "sources" | "help-menu" | "decisions-note";

const ACTING_STEP: WalkthroughStep = {
  title: "You are acting in a seat",
  body: "LoanShip still knows it is you. Every action you take here is recorded under your name plus this seat, and you can stop acting from the banner at any time.",
  target: "stop-acting",
};

const QUEUE_STEP: WalkthroughStep = {
  title: "Prepared for you",
  body: "Assistants prepare work for your seat; you approve, send or delegate it. Nothing leaves LoanShip without a person deciding. Until an assistant is connected for this role, the queue stays empty and says so.",
  target: "prepared-queue",
};

const SOURCES_STEP: WalkthroughStep = {
  title: "Where your work lives",
  body: "This list shows which parts of your work are in LoanShip today and which still happen in Salesforce or Encompass during the pilot.",
  target: "sources",
};

const DECISIONS_STEP: WalkthroughStep = {
  title: "Decisions stay where they are made",
  body: "During the pilot, loan decisions are made in Salesforce and Encompass. Nothing you do in LoanShip is a lending or underwriting decision.",
  target: "decisions-note",
};

const HELP_STEP: WalkthroughStep = {
  title: "Help is always one click away",
  body: "Open this tour again, or go to your role home, from the help menu in the header.",
  target: "help-menu",
};

const ASSIGNED_ONLY = (seat: string): WalkthroughStep => ({
  title: "Your loans",
  body: `As ${seat} you see only the loans someone assigned to you in this seat, and you can't edit them. An RM, an operations manager or a superadmin makes the assignment from the loan's Assigned staff section.`,
  target: "sources",
});

const STEPS: Partial<Record<Role, WalkthroughStep[]>> = {
  ae: [
    { title: "Welcome to LoanShip", body: "Your pipeline is the grid of your loans. Edits save as you type, and your team sees them live." },
    QUEUE_STEP,
    { title: "LO signatures", body: "You prepare 1003 signature requests and approve each send. Nothing goes out without your approval." },
    SOURCES_STEP,
    HELP_STEP,
  ],
  rm: [
    { title: "Welcome to LoanShip", body: "The Team page shows your team's pipeline and goals. You can edit your team's loans." },
    { title: "Assigning pilot staff", body: "Open a loan and use Assigned staff to give a credit analyst, underwriter or other seat access to just that loan." },
    QUEUE_STEP,
    { title: "Security & Audit", body: "See who looked at or changed your team's loans. Every access is on the record." },
    HELP_STEP,
  ],
  admin: [
    { title: "Welcome, superadmin", body: "Admin holds people, roles and teams. The Acting roles section decides which pilot member may act in which seat." },
    { title: "Acting in a seat", body: "Switch into a seat from the header to see and do what that role does. While you act, you have only that seat's powers, not superadmin ones, until you stop.", target: "role-switcher" },
    QUEUE_STEP,
    { title: "Security & Audit", body: "The access log records the real person and, while acting, the seat. Separation of duty follows the person: nobody approves their own proposal in any seat." },
    HELP_STEP,
  ],
  operations_manager: [
    { title: "Operations manager", body: "You see your team's pipeline and can edit its loans. You assign pilot staff to loans from each loan's Assigned staff section." },
    { ...QUEUE_STEP, body: "Assistants will prepare operations work for you here: you approve it, send it or delegate it. No assistant is connected for this seat yet, so the queue is empty." },
    SOURCES_STEP,
    HELP_STEP,
  ],
  credit_analyst: [ASSIGNED_ONLY("a credit analyst"), QUEUE_STEP, { ...DECISIONS_STEP, body: "During the pilot the credit analyst works the file in Salesforce after the Pre-Qual handoff. LoanShip shows your assigned loans; it records no lending decision." }, HELP_STEP],
  underwriter: [ASSIGNED_ONLY("an underwriter"), QUEUE_STEP, { ...DECISIONS_STEP, body: "Underwriting decisions and conditions stay in Encompass during the pilot. Acting as an underwriter in LoanShip never records an underwriting decision." }, HELP_STEP],
  doc_funding: [ASSIGNED_ONLY("docs & funding"), QUEUE_STEP, { ...DECISIONS_STEP, body: "Closing documents and funding stay in Encompass during the pilot." }, HELP_STEP],
  account_manager: [ASSIGNED_ONLY("an account manager"), { ...SOURCES_STEP, body: "Open Conditions to review linked evidence and AI advice, then choose Clear, Not cleared or Ask. The AI recommends; you make the final call. LoanShip runs in parallel with Salesforce and Encompass." }, QUEUE_STEP, HELP_STEP],
};

const DEFAULT_STEPS: WalkthroughStep[] = [{ title: "Your role home", body: "This page shows what's waiting for you and where your work lives." }, QUEUE_STEP, SOURCES_STEP, HELP_STEP];

/** The walkthrough for a role; the acting step comes first while acting. */
export function walkthroughFor(role: Role, acting: boolean): WalkthroughStep[] {
  const steps = STEPS[role] ?? DEFAULT_STEPS;
  return acting ? [ACTING_STEP, ...steps] : steps;
}

/** Contextual tooltips on a role's key controls. */
export function tooltipFor(target: TourTarget, role: Role): string {
  switch (target) {
    case "role-switcher":
      return "Act in another role's seat. A superadmin decides who may act as what; LoanShip records you plus the seat.";
    case "stop-acting":
      return "Go back to your own role. One click; it is recorded.";
    case "prepared-queue":
      return role === "operations_manager"
        ? "Work prepared for you to approve, send or delegate. Nothing happens until you decide."
        : "Work an assistant prepared for this seat. You decide; nothing is sent on its own.";
    case "sources":
      return "Which parts of this role's work are in LoanShip today, and which are still outside it.";
    case "help-menu":
      return "Reopen the walkthrough for this role, or go to your role home.";
    case "decisions-note":
      return "LoanShip is not the system of record during the pilot. It never records a lending or underwriting decision.";
  }
}
